import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
} from "lucide-react"
import { type Table } from "@tanstack/react-table"

import { Button } from "../button"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../select"

interface DataGridPaginationProps<TData> {
    table: Table<TData>
    manualPagination?: boolean
    // Select-all-across-pages props
    isAllAcrossPagesSelected?: boolean
    totalSelectableCount?: number
    // Actual selected count from rowSelection state (for cross-page selections)
    actualSelectedCount?: number
}

export function DataGridPagination<TData>({
    table,
    manualPagination = false,
    isAllAcrossPagesSelected = false,
    totalSelectableCount,
    actualSelectedCount,
}: DataGridPaginationProps<TData>) {
    // For manual pagination, we use the raw row count
    // For client-side, we use the filtered row count
    const totalRows = manualPagination
        ? table.getRowCount()
        : (table.getFilteredRowModel?.()?.rows.length ?? table.getRowModel().rows.length)

    // For selected rows:
    // 1. If all across pages are selected, use totalSelectableCount
    // 2. If actualSelectedCount is provided (cross-page selection), use it
    // 3. Otherwise, fall back to table's filtered selected row count
    const selectedRows = isAllAcrossPagesSelected && totalSelectableCount
        ? totalSelectableCount
        : actualSelectedCount !== undefined
            ? actualSelectedCount
            : table.getFilteredSelectedRowModel().rows.length

    // Calculate page count - if getPageCount returns -1, calculate it manually
    const pageCount = table.getPageCount()
    const calculatedPageCount = pageCount === -1 
        ? Math.max(1, Math.ceil(totalRows / table.getState().pagination.pageSize))
        : pageCount

    return (
        <div className="flex items-center justify-between px-2">
            <div className="flex-1 text-sm text-muted-foreground">
                {selectedRows > 0 ? (
                    <>
                        {selectedRows} of {totalRows} row(s) selected.
                    </>
                ) : (
                    <>
                        Showing {table.getRowModel().rows.length} of {totalRows} row(s).
                    </>
                )}
            </div>
            <div className="flex items-center space-x-6 lg:space-x-8">
                <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium">Rows per page</p>
                    <Select
                        value={`${table.getState().pagination.pageSize}`}
                        onValueChange={(value) => {
                            table.setPageSize(Number(value))
                        }}
                    >
                        <SelectTrigger className="h-8 w-[70px]">
                            <SelectValue placeholder={`${table.getState().pagination.pageSize}`} />
                        </SelectTrigger>
                        <SelectContent>
                            {[10, 20, 30, 40, 50].map((pageSize) => (
                                <SelectItem key={pageSize} value={`${pageSize}`}>
                                    {pageSize}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                    Page {table.getState().pagination.pageIndex + 1} of{" "}
                    {calculatedPageCount}
                </div>
                <div className="flex items-center space-x-2">
                    <Button
                        variant="outline"
                        className="hidden h-8 w-8 p-0 lg:flex"
                        onClick={() => table.setPageIndex(0)}
                        disabled={table.getState().pagination.pageIndex === 0}
                    >
                        <span className="sr-only">Go to first page</span>
                        <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        className="h-8 w-8 p-0"
                        onClick={() => table.previousPage()}
                        disabled={table.getState().pagination.pageIndex === 0}
                    >
                        <span className="sr-only">Go to previous page</span>
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        className="h-8 w-8 p-0"
                        onClick={() => table.nextPage()}
                        disabled={table.getState().pagination.pageIndex >= calculatedPageCount - 1}
                    >
                        <span className="sr-only">Go to next page</span>
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        className="hidden h-8 w-8 p-0 lg:flex"
                        onClick={() => table.setPageIndex(calculatedPageCount - 1)}
                        disabled={table.getState().pagination.pageIndex >= calculatedPageCount - 1}
                    >
                        <span className="sr-only">Go to last page</span>
                        <ChevronsRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    )
}
